<script setup lang="ts">
  import { computed, useAttrs, useCssModule } from 'vue';

  import { StashCommonColor, StashPrimaryColorGroup } from '../../../types';
  import { IconName } from '../Icon/Icon.types';
  import Icon from '../Icon/Icon.vue';
  import Select from '../Select/Select.vue';

  defineOptions({
    name: 'll-select-status',
  });

  export type SelectStatusOption = {
    id: number | string | boolean;
    name: string;
    color?: StashPrimaryColorGroup | StashCommonColor; // todo: just `StashPrimaryColor`?.
    icon?: IconName;
  };

  export interface SelectStatusProps {
    /**
     * Disables the component, if true
     */
    disabled?: boolean;
    /**
     * Which key of the prop option to use for display
     */
    displayBy?: string;
    /**
     * Sets the background color of the component to the color of the selected option
     */
    secondary?: boolean;
    /**
     * Allows option text to wrap to next line when set to true
     */
    noTruncate?: boolean;
    /**
     * Placeholder text
     */
    placeholder?: string;
    /**
     * The list of all options to select the status from
     * each object must have the following structure: { id: number | string, name: string, color?: string }
     */
    statusOptions: Array<SelectStatusOption>;
    /**
     * Default field to track the selected status by
     */
    trackBy?: string;
    /**
     * Sets the currently-selected option for the component
     */
    modelValue: number | string | boolean;
    /**
     * @deprecated Use `:model-value` or `v-model` instead of `:value`.
     */
    value?: number | string | boolean | null;

    /**
     * Shows a label above the select
     */
    label?: string;
  }

  /**
   * Array containing the available dark fill colors that need a different text color.
   */
  const DARK_FILL_COLORS = ['green', 'teal', 'ice', 'yellow', 'seafoam', 'seafoam-700'];

  const attrs = useAttrs();
  const classes = useCssModule();

  const props = withDefaults(defineProps<SelectStatusProps>(), {
    disabled: false,
    displayBy: 'name',
    secondary: false,
    noTruncate: false,
    placeholder: 'Select Status',
    trackBy: 'id',
    value: null,
    label: undefined,
  });

  if (props.value) {
    throw new Error('ll-select-status: use :model-value or v-model instead of :value.');
  }

  if (attrs.onInput) {
    throw new Error('ll-select-status: use the @update:model-value event instead of @input');
  }

  const emit = defineEmits<{
    /**
     * Emitted when the model value changes
     */
    (e: 'update:model-value', value: unknown): void;
  }>();

  const valueOption = computed(() => {
    return props.statusOptions.find((status) => status[props.trackBy] === props.modelValue);
  });

  const hasDarkFill = computed(() => {
    const { color = '' } = valueOption.value || {};
    return DARK_FILL_COLORS.includes(color);
  });

  /**
   * @param {string} color Valid color name
   * @return {string} tailwind class for given color
   */
  function getBgColor(color: SelectStatusOption['color']): string {
    // if there's no hyphen then it's a color name and has no shade, so we default to 500
    return color?.includes('-') ? `bg-${color}` : `bg-${color}-500`;
  }

  /**
   * @param {string} color Valid color name
   * @return {string} tailwind class for given color
   */
  function getTextColor(color: SelectStatusOption['color']): string {
    // if there's no hyphen then it's a color name and has no shade, so we default to 500
    return color?.includes('-') ? `text-${color}` : `text-${color}-500`;
  }

  function onSelect(value?: SelectStatusOption): void {
    emit('update:model-value', value?.[props.trackBy] ?? '');
  }
</script>

<template>
  <Select
    ref="select"
    hide-search
    single
    :class="[
      classes.select,
      {
        'stash-select-status--filled': props.secondary,
        'stash-select-status--filled-dark': props.secondary && hasDarkFill,
      },
    ]"
    :disabled="props.disabled"
    :icon="!props.disabled && 'caret-down'"
    :options="props.statusOptions"
    :placeholder="props.placeholder"
    :track-by="props.trackBy"
    :model-value="valueOption"
    :label="props.label"
    data-test="stash-select-status"
    class="stash-select-status"
    @update:model-value="onSelect"
  >
    <template #selected="{ option }">
      <div
        v-if="props.secondary"
        class="stash-select-status--selected absolute inset-0 flex size-full items-center rounded pl-3"
        data-test="select-status|value"
        :class="[getBgColor(option.color)]"
      >
        <span class="mr-6 truncate">{{ option[props.displayBy] }}</span>
      </div>

      <div v-else class="flex items-center" data-test="select-status|value">
        <Icon
          data-test="select-status|value-icon"
          :name="option.icon || 'circle-status'"
          :class="[
            { [getTextColor(option.color)]: option.color },
            { 'text-ice-700': !option.color && !option.hexCode },
          ]"
          :style="{ color: option.hexCode }"
        />
        <span class="ml-1.5 mr-6 truncate">{{ option[props.displayBy] }}</span>
      </div>
    </template>

    <template #option="{ option }">
      <div class="flex min-w-0">
        <Icon
          data-test="svg|status-color"
          :name="option.icon || 'circle-status'"
          :class="[
            { [getTextColor(option.color)]: option.color },
            { 'text-ice-700': !option.color && !option.hexCode },
          ]"
          class="!shrink-0"
          :style="{ color: option.hexCode }"
        />
        <span data-test="span|option-text" class="ml-1.5" :class="!props.noTruncate && 'truncate'">
          {{ option[props.displayBy] }}
        </span>
      </div>
    </template>
  </Select>
</template>

<style module>
  @layer utilities {
    :global(.stash-select-status--filled .stash-select__content) {
      border: none;
      padding: 0;
    }

    :global(
      .stash-select-status--filled
        .stash-select--active
        .stash-select__content:focus-within
        .stash-select__border-selector
    ) {
      box-sizing: content-box;
      max-width: calc(100% - 2px);
    }

    :global(.stash-select-status--filled .stash-select-status--selected),
    :global(.stash-select-status--filled :has(.stash-select-status--selected) .icon--caret-down) {
      color: var(--color-white);
    }

    :global(.stash-select-status--filled-dark .stash-select-status--selected),
    :global(.stash-select-status--filled-dark :has(.stash-select-status--selected) .icon--caret-down) {
      color: var(--color-ice-900);
    }

    .select :global(.stash-select--disabled > .stash-select__content) {
      background-color: transparent;
      border-color: transparent;
    }

    .select :global(.stash-select__chips) {
      margin: 0;
    }

    .select :global(.stash-select__options) {
      margin: 0;
      padding: 0;
    }

    .select ul {
      position: relative;
    }
  }
</style>
